import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";

const MAX_POLLS = 12;

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");

  useEffect(() => {
    if (cancelled || !sessionId) {
      if (cancelled) trackEvent("Abandoned payment (cancelled at Stripe)");
      if (!cancelled && !sessionId) setState("error");
      return;
    }
    let polls = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          trackEvent("Paid enrollment fee");
          return setState("paid");
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          trackEvent(`Payment ${data.payment_status}`);
          return setState("error");
        }
      } catch {
        return setState("error");
      }
      polls += 1;
      if (polls >= MAX_POLLS) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled]);

  return (
    <div className="flex min-h-screen flex-col items-start px-6 py-14 text-left sm:px-14">
      <Link to="/" className="mb-12">
        <Brand />
      </Link>
      <Card className="w-full max-w-lg border-slate-200">
        <CardContent className="p-8" data-testid="payment-result">
          {state === "checking" && (
            <div className="flex items-center gap-3 text-slate-700">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Confirming your payment…</span>
            </div>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">
                Enrollment complete
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Thank you. Your payment has been received and your family record has been created with
                Alabama Church School. Your educational partner will submit the student's annual
                academic records package when the school year is complete.
              </p>
            </>
          )}
          {state === "cancelled" && (
            <>
              <XCircle className="h-9 w-9 text-amber-500" />
              <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">
                Payment cancelled
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                Your enrollment details were saved but payment was not completed. You can return to
                your partner's enrollment page to try again.
              </p>
            </>
          )}
          {(state === "error" || state === "timeout") && (
            <>
              <XCircle className="h-9 w-9 text-red-500" />
              <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">
                We could not confirm payment
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                If you were charged, Alabama Church School will confirm your enrollment shortly.
                Please contact the school office if this persists.
              </p>
            </>
          )}
          <Link to="/" className="mt-7 inline-block">
            <Button variant="outline" data-testid="payment-home-button">
              Return home
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
